// SHARED-010: Type-safe Phaser↔React event bus

import Phaser from 'phaser';

export interface BattleEvents {
  // Phaser → React
  matchComplete: { matches: number; combo: number; damage: number };
  damageDealt: { total: number; isCrit: boolean };
  skillTriggered: { agentId: string; skillName: string };
  turnEnd: { turn: number; enemyHp: number; maxHp: number };
  battleEnd: { outcome: 'VICTORY' | 'DEFEAT' };
  boardReady: undefined;
  cascadeStep: { step: number; matches: number };
  overloadTriggered: { affectedAgentIds: string[] };

  // Staged cascade round feedback (Phaser → React, emitted per-round during animation)
  cascadeRoundComplete: {
    chainDepth: number;
    roundWeight: number;
    weightTier: number;
    comboMultiplier: number;
    roundDamage: number;
    isCrit: boolean;
  };

  // React → Phaser
  playerSwap: { from: { x: number; y: number }; to: { x: number; y: number } };
  restartBattle: undefined;
  pauseBattle: undefined;
  resumeBattle: undefined;
}

class TypedEventBus {
  private emitter = new Phaser.Events.EventEmitter();

  emit<K extends keyof BattleEvents>(
    event: K,
    ...args: BattleEvents[K] extends undefined ? [] : [BattleEvents[K]]
  ): void {
    this.emitter.emit(event, ...args);
  }

  on<K extends keyof BattleEvents>(
    event: K,
    fn: BattleEvents[K] extends undefined ? () => void : (data: BattleEvents[K]) => void,
  ): void {
    this.emitter.on(event, fn as (...args: unknown[]) => void);
  }

  off<K extends keyof BattleEvents>(
    event: K,
    fn?: BattleEvents[K] extends undefined ? () => void : (data: BattleEvents[K]) => void,
  ): void {
    if (fn) {
      this.emitter.off(event, fn as (...args: unknown[]) => void);
    } else {
      this.emitter.removeAllListeners(event);
    }
  }

  removeAllListeners(): void {
    this.emitter.removeAllListeners();
  }
}

export const EventBus = new TypedEventBus();
